import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button, ButtonWithLoading } from '@actual-app/components/button';
import { Input } from '@actual-app/components/input';
import { View } from '@actual-app/components/view';
import { validateIncomeDistributionRules } from '@actual-app/finance-engine';
import type { IncomeDistributionRule } from '@actual-app/finance-engine';

import type {
  FinancialManagementState,
  ProductFinanceClient,
} from '#components/product-finance/client';
import {
  formatMoneyInput,
  parseMoneyInput,
} from '#components/product-finance/money';

import {
  errorStyle,
  Field,
  formStyle,
  helpStyle,
  inputStyle,
  ManagementCard,
} from './management-shared';

type DraftRule = Omit<IncomeDistributionRule, 'value'> & { value: string };

export function IncomeDistributionRules({
  state,
  client,
}: {
  state: FinancialManagementState;
  client: ProductFinanceClient;
}) {
  const { t } = useTranslation();
  const [rules, setRules] = useState<readonly DraftRule[]>([]);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>(
    'loading',
  );
  const [reloadVersion, setReloadVersion] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [error, setError] = useState<'invalid' | 'save' | null>(null);
  const isAvailable =
    client.loadIncomeDistributionRules !== undefined &&
    client.saveIncomeDistributionRules !== undefined;

  useEffect(() => {
    let isCurrent = true;
    setStatus('loading');
    if (client.loadIncomeDistributionRules === undefined) {
      setStatus('error');
      return;
    }
    void client
      .loadIncomeDistributionRules()
      .then(saved => {
        if (isCurrent) {
          setRules(
            saved.map(rule => ({
              ...rule,
              value: formatMoneyInput({
                minorUnits: rule.value,
                currency: rule.currency,
              }),
            })),
          );
          setStatus('ready');
        }
      })
      .catch(() => {
        if (isCurrent) setStatus('error');
      });
    return () => {
      isCurrent = false;
    };
  }, [client, reloadVersion]);

  function changeRules(next: readonly DraftRule[]) {
    setRules(next);
    setIsSaved(false);
    setError(null);
  }

  function updateRule(id: string, patch: Partial<DraftRule>) {
    changeRules(
      rules.map(rule => (rule.id === id ? { ...rule, ...patch } : rule)),
    );
  }

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (isSaving || client.saveIncomeDistributionRules === undefined) return;
    let parsed: readonly IncomeDistributionRule[];
    try {
      parsed = rules.map(rule => {
        const value = parseMoneyInput(rule.value);
        if (
          value === null ||
          !state.goals.some(summary => summary.goal.id === rule.goalId)
        ) {
          throw new Error('Invalid distribution rule.');
        }
        return { ...rule, value };
      });
      validateIncomeDistributionRules(parsed);
    } catch {
      setError('invalid');
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      await client.saveIncomeDistributionRules({ rules: parsed });
      setIsSaved(true);
    } catch {
      setError('save');
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <ManagementCard title={<Trans>Income distribution</Trans>}>
      <p style={helpStyle}>
        <Trans>
          Rules only suggest savings. You confirm each distribution when
          recording income.
        </Trans>
      </p>
      {status === 'loading' && (
        <output style={{ display: 'block' }}>
          <Trans>Loading distribution rules…</Trans>
        </output>
      )}
      {status === 'error' && (
        <View style={{ gap: 8 }}>
          <p role="alert">
            <Trans>Distribution rules could not be loaded.</Trans>
          </p>
          <Button
            isDisabled={!isAvailable}
            onPress={() => setReloadVersion(reloadVersion + 1)}
          >
            <Trans>Try again</Trans>
          </Button>
        </View>
      )}
      {status === 'ready' && (
        <form onSubmit={save} style={formStyle}>
          <fieldset
            disabled={isSaving}
            style={{
              border: 0,
              padding: 0,
              margin: 0,
              minWidth: 0,
              display: 'grid',
              gap: 12,
            }}
          >
            <legend>
              <Trans>Savings rules</Trans>
            </legend>
            {rules.length === 0 && (
              <p style={helpStyle}>
                <Trans>No distribution rules yet.</Trans>
              </p>
            )}
            {rules.map((rule, index) => (
              <fieldset
                key={rule.id}
                style={{
                  border: 0,
                  padding: 0,
                  margin: 0,
                  minWidth: 0,
                  display: 'grid',
                  gap: 8,
                }}
              >
                <legend>
                  <Trans>Rule {{ number: index + 1 }}</Trans>
                </legend>
                <Field
                  label={<Trans>Savings destination</Trans>}
                  htmlFor={`distribution-goal-${rule.id}`}
                >
                  <select
                    id={`distribution-goal-${rule.id}`}
                    value={rule.goalId}
                    style={inputStyle}
                    onChange={event =>
                      updateRule(rule.id, { goalId: event.target.value })
                    }
                  >
                    <option value="">
                      <Trans>Choose a savings goal</Trans>
                    </option>
                    {state.goals.map(summary => (
                      <option key={summary.goal.id} value={summary.goal.id}>
                        {summary.goal.name}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field
                  label={<Trans>Apply to income</Trans>}
                  htmlFor={`distribution-source-${rule.id}`}
                >
                  <select
                    id={`distribution-source-${rule.id}`}
                    aria-label={t('Apply to income')}
                    value={rule.source}
                    style={inputStyle}
                    onChange={event => {
                      const source = event.target.value;
                      if (
                        source === 'fixed' ||
                        source === 'extra' ||
                        source === 'both'
                      ) {
                        updateRule(rule.id, { source });
                      }
                    }}
                  >
                    <option value="fixed">
                      <Trans>Fixed income</Trans>
                    </option>
                    <option value="extra">
                      <Trans>Extra income</Trans>
                    </option>
                    <option value="both">
                      <Trans>Fixed and extra income</Trans>
                    </option>
                  </select>
                </Field>
                <Field
                  label={<Trans>Distribution method</Trans>}
                  htmlFor={`distribution-kind-${rule.id}`}
                >
                  <select
                    id={`distribution-kind-${rule.id}`}
                    aria-label={t('Distribution method')}
                    value={rule.kind}
                    style={inputStyle}
                    onChange={event => {
                      const kind = event.target.value;
                      if (kind === 'fixed-amount' || kind === 'percentage') {
                        updateRule(rule.id, { kind, value: '' });
                      }
                    }}
                  >
                    <option value="fixed-amount">
                      <Trans>Fixed amount</Trans>
                    </option>
                    <option value="percentage">
                      <Trans>Percentage</Trans>
                    </option>
                  </select>
                </Field>
                <Field
                  label={
                    rule.kind === 'percentage' ? (
                      <Trans>Percentage of received income</Trans>
                    ) : (
                      <Trans>Amount to set aside</Trans>
                    )
                  }
                  htmlFor={`distribution-value-${rule.id}`}
                >
                  <Input
                    id={`distribution-value-${rule.id}`}
                    inputMode="decimal"
                    required
                    value={rule.value}
                    onChangeValue={value => updateRule(rule.id, { value })}
                  />
                </Field>
                <Button
                  type="button"
                  onPress={() =>
                    changeRules(rules.filter(item => item.id !== rule.id))
                  }
                  aria-label={t('Remove rule {{number}}', {
                    number: index + 1,
                  })}
                >
                  <Trans>Remove rule</Trans>
                </Button>
              </fieldset>
            ))}
            <Button
              type="button"
              isDisabled={state.goals.length === 0}
              onPress={() =>
                changeRules([
                  ...rules,
                  {
                    id: crypto.randomUUID(),
                    goalId: state.goals[0]?.goal.id ?? '',
                    source: 'both',
                    kind: 'percentage',
                    value: '',
                    currency: state.settings.currency,
                  },
                ])
              }
            >
              <Trans>Add distribution rule</Trans>
            </Button>
          </fieldset>
          {state.goals.length === 0 && (
            <p style={helpStyle}>
              <Trans>Create a savings goal or emergency fund first.</Trans>
            </p>
          )}
          {error !== null && (
            <p role="alert" style={errorStyle}>
              {error === 'invalid' ? (
                <Trans>
                  Choose a goal and positive amounts. Percentages for each
                  income type must total at most 100%.
                </Trans>
              ) : (
                <Trans>
                  We could not save the distribution rules. Try again.
                </Trans>
              )}
            </p>
          )}
          {isSaved && (
            <output style={{ display: 'block' }}>
              <Trans>Distribution rules saved.</Trans>
            </output>
          )}
          <ButtonWithLoading
            type="submit"
            variant="primary"
            isDisabled={isSaving || !isAvailable}
            isLoading={isSaving}
          >
            <Trans>Save distribution rules</Trans>
          </ButtonWithLoading>
        </form>
      )}
    </ManagementCard>
  );
}
